import DoubleArrowIcon from '@mui/icons-material/DoubleArrow';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Button,
  Grid,
  Link,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import ResourcesRightSidebar from '@/components/nav/ResourcesRightSidebar';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const FormW2GInstructions = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="IRS Form W-2G instructions for Tax Year 2025"
          description="Learn IRS Form W-2G step-by-step instructions for Tax Year 2025. TaxZerone helps you accurately report gambling winnings and withheld taxes."
          keywords=""
          canonical="resources/information-returns/form-w-2g-instructions/"
          ogType="article"
          jsonLdPricing="2.49"
          jsonLdAggregateRatingValue="5.00"
          jsonLdAggregateRatingCount="57448"
        />
      }
    >
      <Container maxWidth="lg" sx={{ textAlign: 'center' }}>
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,

            mt: 3,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight"> Form W-2G</span>
          </span>{' '}
          Instructions
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
            fontWeight: 400,
            mt: 1,
            mb: 2,
          }}
        >
          Report your Gambling Winnings with Ease!
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2Ginstructions`}
          >
            E-File Now
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
        }}
        spacing={2}
        m={0}
        mt={0}
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={0}
            rowSpacing={0}
            m={0}
            sx={{ flexDirection: 'row-reverse' }}
          >
            <Grid
              size={{ xs: 12, md: 3.5, sm: 4.5 }}
              sx={{ display: { xs: 'none', sm: 'block' } }}
            >
              <ResourcesRightSidebar />
            </Grid>
            <Grid size={{ xs: 12, md: 8.5, sm: 7.5 }}>
              <Paper
                variant="outlined"
                sx={{
                  border: '1px solid #ccc',
                  p: 3,
                  px: 3,
                  textAlign: 'left',
                  mb: 0,
                }}
              >
                <Box
                  itemScope
                  itemType="https://schema.org/Article"
                  sx={{
                    p: 3,
                    px: 3,
                    backgroundColor: '#f0f5f8',
                    borderLeft: '5px solid #01456c',
                    mb: 4,
                    borderRadius: 3,
                    position: 'relative',
                  }}
                >
                  <link
                    itemProp="mainEntityOfPage"
                    href="https://www.taxzerone.com/resources/information-returns/form-w-2g-instructions/"
                  />

                  {/* Article requires an image */}
                  <div
                    itemProp="image"
                    itemScope
                    itemType="https://schema.org/ImageObject"
                    style={{ display: 'none' }}
                  >
                    <link
                      itemProp="url"
                      href="https://www.taxzerone.com/assets/images/logo.webp"
                    />
                    <data itemProp="width" value="380" />
                    <data itemProp="height" value="124" />
                  </div>

                  <Box
                    sx={{
                      display: 'grid',
                      justifyContent: 'flex-start',
                      alignItems: 'center',
                      textAlign: 'left',
                      mb: 2,
                    }}
                  >
                    <Typography variant="body2">
                      Last updated:{' '}
                      <time
                        itemProp="datePublished"
                        dateTime="2026-07-07T09:00:00-05:00"
                      >
                        July 7, 2026
                      </time>
                      <time
                        itemProp="dateModified"
                        dateTime="2026-07-07T09:00:00-05:00"
                        style={{ display: 'none' }}
                      >
                        July 7, 2026
                      </time>
                    </Typography>

                    <Typography
                      variant="body2"
                      sx={{ fontWeight: 600 }}
                      itemProp="author"
                      itemScope
                      itemType="https://schema.org/Person"
                    >
                      -by{' '}
                      <span itemProp="name">
                        <a
                          itemProp="url"
                          href={`${process.env.NEXT_PUBLIC_HOMESITE_URL}`}
                          style={{ color: 'inherit', textDecoration: 'none' }}
                        >
                          Alexia Zepeda
                        </a>
                      </span>{' '}
                      | TaxZerone
                    </Typography>
                  </Box>

                  <Typography
                    variant="h4"
                    itemProp="headline"
                    sx={{
                      fontWeight: 600,
                      mb: 2,
                      fontSize: { md: 20, sm: 18, xs: 16 },
                      color: '#01456c',
                      fontStyle: 'italic',
                    }}
                  >
                    Form W-2G at a Glance
                  </Typography>

                  <Box
                    component="ul"
                    itemScope
                    itemType="https://schema.org/ItemList"
                    sx={{
                      mb: 0,
                      pl: 0,
                      listStyle: 'none',

                      '& li': {
                        mb: 1.6,
                        position: 'relative',
                        pl: 3,
                      },

                      '& li::before': {
                        content: '"★"',
                        position: 'absolute',
                        left: 0,
                        top: '-2px',
                        color: '#01456c',
                        fontSize: '18px',
                      },
                    }}
                  >
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="1" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>
                          Form W-2G, Certain Gambling Winnings
                        </strong>
                        , is used by gambling payers to report gambling winnings
                        and any federal income tax withheld from winners.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="2" />
                      <span itemProp="name">
                        {'Recipient copies must be '}
                        furnished
                        {' by '}
                        <strong style={{ fontWeight: 600 }}>
                          {'January '}
                        </strong>
                        <strong style={{ fontWeight: 600 }}>31;</strong>
                        {' the IRS filing deadline is '}
                        <strong style={{ fontWeight: 600 }}>February 28</strong>
                        {' for paper filings and '}
                        <strong style={{ fontWeight: 600 }}>March 31</strong>
                        {' for electronic filings.'}
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="3" />
                      <span itemProp="name">
                        {
                          'This article covers line-by-line instructions, including payer and winner information, '
                        }
                        <strong style={{ fontWeight: 600 }}>Box 1</strong>
                        {' (Reportable Gambling Winnings)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 2 '}</strong>
                        (Date won)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 3</strong>
                        {' (Type of Wager)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 4 '}</strong>(
                        {'Federal '}
                        Income
                        {' tax withheld'}){', '}
                        <strong style={{ fontWeight: 600 }}>Box 5</strong>
                        {' (Transaction)'}
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 6 '}</strong>
                        (Race)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 7 '}</strong>
                        (Winnings From Identical Wagers)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 8 '}</strong>
                        (Cashier)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>Box 9</strong>
                        (Winner's Taxpayer
                        <strong style={{ fontWeight: 600 }}> </strong>
                        Identification Number)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 10 '}</strong>
                        (Window)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 11 '}</strong>
                        (First ID)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 12 '}</strong>
                        (Second ID)
                        {', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 13 '}</strong>
                        (State / Payer’s ID ){', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 14 '}</strong>
                        (State
                        {' Winnings'}){', '}
                        <strong style={{ fontWeight: 600 }}>{'Box 15 '}</strong>
                        (State Income tax withheld ){', and '}
                        <strong style={{ fontWeight: 600 }}>{'Box 16 '}</strong>
                        (Local Winnings)
                        <strong style={{ fontWeight: 600 }}>
                          {', Box 17 '}
                        </strong>
                        (Local Income Tax withheld)
                        <strong style={{ fontWeight: 600 }}>
                          {', Box 18 '}
                        </strong>
                        (Name of Locality).
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="4" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Penalties:</strong>
                        {
                          ' Late, incorrect, or incomplete filing of Form W-2G may result in IRS penalties ranging from '
                        }
                        <strong style={{ fontWeight: 600 }}>
                          $60 to $680 per return
                        </strong>
                        , depending on when the return is filed, whether
                        corrections are required, and whether the filer complies
                        with electronic filing requirements.
                      </span>
                    </li>
                    <li
                      itemProp="itemListElement"
                      itemScope
                      itemType="https://schema.org/ListItem"
                    >
                      <meta itemProp="position" content="5" />
                      <span itemProp="name">
                        <strong style={{ fontWeight: 600 }}>Extensions:</strong>{' '}
                        <Link
                          href="/tax-extension/efile-irs-form-8809-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 8809
                          </strong>
                        </Link>
                        {' to request an automatic filing extension. '}
                        <Link
                          href="/tax-extension/file-irs-form-15397-online/"
                          sx={{ color: '#0202ff' }}
                        >
                          <strong style={{ fontWeight: 600 }}>
                            File Form 15397
                          </strong>
                        </Link>{' '}
                        {'to request the full '}
                        <strong style={{ fontWeight: 600 }}>
                          30-day extension
                        </strong>
                        {' for furnishing recipient copies'}.
                      </span>
                    </li>
                  </Box>

                  <div
                    itemProp="publisher"
                    itemScope
                    itemType="https://schema.org/Organization"
                    style={{ display: 'none' }}
                  >
                    <span itemProp="name">TaxZerone</span>
                    <div
                      itemProp="logo"
                      itemScope
                      itemType="https://schema.org/ImageObject"
                    >
                      <link
                        itemProp="url"
                        href="https://www.taxzerone.com/assets/images/logo.webp"
                      />
                      <data itemProp="width" value="380" />
                      <data itemProp="height" value="124" />
                    </div>
                  </div>
                </Box>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  If you’ve made certain gambling winnings payouts, you’re
                  required to report them using <br /> IRS Form W-2G. This form
                  is used to report winnings from lotteries, sweepstakes, horse
                  races, casinos, and other wagering activities. Timely and
                  accurate filing helps ensure compliance with IRS regulations
                  and avoids penalties.{' '}
                </Typography>
                <Box
                  sx={{
                    pt: 2,
                  }}
                >
                  <Typography variant="body1" sx={{ mb: 0.5 }}>
                    <b>Table of Contents </b>
                  </Typography>
                  <List
                    sx={{ mb: 5, border: '1px solid #ccc', borderRadius: 2 }}
                  >
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#whatis" sx={{ color: '#0202ff' }}>
                          What is Form W-2G?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#who" sx={{ color: '#0202ff' }}>
                          Who needs to file Form W-2G?
                        </Link>
                      </ListItemText>
                    </ListItem>
                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link href="#key" sx={{ color: '#0202ff' }}>
                          Important key deadline to file Form W-2G?
                        </Link>
                      </ListItemText>
                    </ListItem>

                    <ListItem sx={{ py: 0.5, alignItems: 'flex-start' }}>
                      <ListItemIcon
                        sx={{ minWidth: 'inherit', mr: 1, mt: 0.6 }}
                      >
                        <DoubleArrowIcon />
                      </ListItemIcon>
                      <ListItemText>
                        <Link
                          href="#form"
                          id="whatis"
                          sx={{ color: '#0202ff' }}
                        >
                          Form W-2G Instructions-How to fill out?
                        </Link>
                      </ListItemText>
                    </ListItem>
                  </List>
                </Box>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  What is Form W-2G?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  {' '}
                  IRS Form W-2G is an IRS information return used to report
                  certain gambling winnings, and any federal income tax withheld
                  from those winnings. This form is typically issued by
                  organizations like casinos, racetracks, state lotteries, or
                  other gambling operators when a player receives a payout that
                  meets IRS reporting thresholds.
                </Typography>
                <Typography variant="body1" id="who" sx={{ mb: 4 }}>
                  The form helps ensure that both the IRS and the recipient
                  accurately report gambling income on their tax returns. Payers
                  are responsible for completing and furnishing Form W-2G to
                  both the winner and the IRS.
                </Typography>
                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Who needs to file Form W-2G?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Payers (such as casinos, lotteries, racetracks, or any
                  organization that pays out qualifying gambling winnings) are
                  the ones required to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w-2g/"
                    sx={{ color: '#0202ff' }}
                  >
                    file Form W-2G
                  </Link>
                  . They file it on behalf of the winners to report the amount
                  won and any taxes withheld.
                </Typography>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Form W-2G must be filed when the winnings meet or exceed the
                  following thresholds:
                </Typography>
                <Box component="ul" sx={{ mb: 3, '& li': { mb: 1.5 } }}>
                  <li>
                    <span style={{ fontWeight: 600 }}> $1,200</span> or more
                    from a slot machine or bingo game (not reduced by wager).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> $1,500</span> or more
                    from a keno game (reduced by the wager).
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> $5,000</span> or more
                    from a poker tournament (reduced by the buy-in or entry
                    fee).{' '}
                  </li>
                  <li>
                    {' '}
                    <span style={{ fontWeight: 600 }}> $600 </span>or more and
                    at least <span style={{ fontWeight: 600 }}>300 times</span>{' '}
                    the wager from other gambling activities (reduced by the
                    wager at the payer&apos;s discretion).
                  </li>
                  <li>
                    <span style={{ fontWeight: 600 }}> Any winnings</span>{' '}
                    subject to{' '}
                    <span style={{ fontWeight: 600 }} id="key">
                      federal income tax withholding
                    </span>{' '}
                    (either regular or backup withholding)
                  </li>
                </Box>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mb: 0.5,
                  }}
                >
                  Important key deadline to file Form W-2G?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  The{' '}
                  <Link
                    href="/resources/information-returns/form-w-2g-due-dates/"
                    sx={{ color: '#0202ff' }}
                  >
                    form W-2G deadline
                  </Link>{' '}
                  is given below:
                </Typography>

                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead>
                        <TableRow sx={{ backgroundColor: '#ccc' }}>
                          <TableCell
                            sx={{
                              color: '#444',
                              width: 300,
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Filing Type
                          </TableCell>

                          <TableCell
                            sx={{ color: '#444', width: 300, fontWeight: 600 }}
                          >
                            Deadlines
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              fontWeight: 600,
                            }}
                          >
                            Sending Winner’s copy
                          </TableCell>

                          <TableCell sx={{}}>February 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Paper filing
                          </TableCell>

                          <TableCell sx={{}}>March 2, 2026 </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              fontWeight: 600,
                              borderRight: '1px solid #828282',
                            }}
                          >
                            IRS e-filing
                          </TableCell>

                          <TableCell sx={{}}>March 31, 2026 </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{ mt: 2, mb: 4, fontSize: 13 }}
                  id="form"
                >
                  <b>Note:</b> If the deadline falls on a weekend or federal
                  holiday, the due date is extended to the next business day.
                </Alert>

                <Typography
                  variant="h2"
                  sx={{
                    fontSize: { lg: 26, md: 22, sm: 20, xs: 18 },
                    fontWeight: 600,
                    mt: 4,
                    mb: 1,
                  }}
                >
                  Form W-2G Instructions-How to fill out?
                </Typography>
                <Typography variant="body1" sx={{ mb: 2 }}>
                  Let&apos;s see detailed instructions to{' '}
                  <Link
                    href="/information-returns/efile-irs-form-w-2g/"
                    sx={{ color: '#0202ff' }}
                  >
                    {' '}
                    fill out Form W-2G
                  </Link>
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 1: Enter the Payer’s details
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-payer-img.webp" />
                  <source srcSet="/assets/images/fw-2g-payer-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-payer-img.png"
                    loading="lazy"
                    alt="Step 1: Enter the Payer’s details"
                    width={525}
                    height={309}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the payer’s name, address and Taxpayer Identification
                  number (TIN) along with the telephone number to ensure
                  accurate reporting and communication of the form submission.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 2: Enter the Winner’s details
                </Typography>

                <picture>
                  <source srcSet="/assets/images/fw-2g-winner-img.webp" />
                  <source srcSet="/assets/images/fw-2g-winner-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-winner-img.png"
                    loading="lazy"
                    alt="Step 2: Enter the Winner’s details"
                    width={524}
                    height={309}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the winner’s name, address including apartment number,
                  city, state, country and Zip code.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Step 3: Gambling Winnings details
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Box 1: Reportable Winnings
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-reportable-img.webp" />
                  <source srcSet="/assets/images/fw-2g-reportable-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-reportable-img.png"
                    loading="lazy"
                    alt="Box 1: Reportable Winnings"
                    width={216}
                    height={79}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount of gambling winnings won by the winner.
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Box 2: Date won
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-date-won-img.webp" />
                  <source srcSet="/assets/images/fw-2g-date-won-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-date-won-img.png"
                    loading="lazy"
                    alt="Box 2: Date won"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the exact date when the winnings are earned (MM/DD/YYYY
                  format).
                </Typography>
                <Typography
                  variant="h3"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 1,
                    fontWeight: 600,
                  }}
                >
                  Box 3: Type of Wager
                </Typography>

                <picture>
                  <source srcSet="/assets/images/fw-2g-type-wager-img.webp" />
                  <source srcSet="/assets/images/fw-2g-type-wager-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-type-wager-img.png"
                    loading="lazy"
                    alt="Box 3: Type of Wager"
                    width={216}
                    height={52}
                    className="img-responsive"
                    sx={{
                      maxWidth: '100%',
                      border: '1px solid #333',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 1 }}>
                  Specify the type of wager. Common examples include:
                </Typography>
                <Box component="div" sx={{ mb: 3 }}>
                  <TableContainer sx={{ border: '1px solid #828282' }}>
                    <Table sx={{}} aria-label="Due Date Chart">
                      <TableHead />
                      <TableBody>
                        <TableRow sx={{}}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                              width: 400,
                            }}
                          >
                            Horse racing
                          </TableCell>

                          <TableCell sx={{}}>Dog racing </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Jai alai
                          </TableCell>

                          <TableCell sx={{}}>Sweepstakes </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Wagering pools
                          </TableCell>

                          <TableCell sx={{}}>Lotteries </TableCell>
                        </TableRow>
                        <TableRow sx={{ backgroundColor: '#f9f9f9' }}>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Bingo
                          </TableCell>

                          <TableCell sx={{}}>Keno </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell
                            sx={{
                              borderRight: '1px solid #828282',
                            }}
                          >
                            Slot machines
                          </TableCell>

                          <TableCell sx={{}}>
                            And any other type of gambling&apos;s{' '}
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 4: Federal Income tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-federal-income-img.webp" />
                  <source srcSet="/assets/images/fw-2g-federal-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-federal-income-img.png"
                    loading="lazy"
                    alt="Box 4: Federal Income tax withheld"
                    width={217}
                    height={52}
                    className="img-responsive"
                    sx={{
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any federal income tax withheld from the winnings, if
                  applicable.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 5: Transaction
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/fw-2g-transaction-img.webp" />
                    <source srcSet="/assets/images/fw-2g-transaction-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/fw-2g-transaction-img.png"
                      loading="lazy"
                      alt="Box 5: Transaction"
                      width={217}
                      height={53}
                      className="img-responsive"
                      sx={{
                        border: '1px solid #333',
                        maxWidth: '100%',
                        mb: 2,
                      }}
                    />
                  </picture>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the ticket or transaction number related to the winning
                  wager.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 6: Race
                </Typography>
                <Box>
                  <picture>
                    <source srcSet="/assets/images/fw-2g-race-img.webp" />
                    <source srcSet="/assets/images/fw-2g-race-img.png" />
                    <Box
                      component="img"
                      src="/assets/images/fw-2g-race-img.png"
                      loading="lazy"
                      alt="Box 6: Race"
                      width={217}
                      height={53}
                      className="img-responsive"
                      sx={{
                        border: '1px solid #333',
                        maxWidth: '100%',
                        mb: 1,
                      }}
                    />
                  </picture>
                </Box>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  If the winnings came from horse or dog racing, include the
                  race number.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 7: Winnings from identical wagers
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-wages-id-img.webp" />
                  <source srcSet="/assets/images/fw-2g-wages-id-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-wages-id-img.png"
                    loading="lazy"
                    alt="Box 7: Winnings from identical wagers"
                    width={216}
                    height={53}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the total amount won from identical wagers grouped
                  together.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 8: Cashier
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-cashier-img.webp" />
                  <source srcSet="/assets/images/fw-2g-cashier-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-cashier-img.png"
                    loading="lazy"
                    alt="Box 8: Cashier"
                    width={217}
                    height={53}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name or ID number of the cashier who paid out the
                  winnings.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 9: Winner’s TIN
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-winning-tin-img.webp" />
                  <source srcSet="/assets/images/fw-2g-winning-tin-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-winning-tin-img.png"
                    loading="lazy"
                    alt="Box 9: Winner’s TIN"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the winner’s Taxpayer Identification Number, usually
                  Social Security Number (SSN).
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 10: Window
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-window-img.webp" />
                  <source srcSet="/assets/images/fw-2g-window-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-window-img.png"
                    loading="lazy"
                    alt="Box 10: Window"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the window number or payout location at which the
                  winnings were issued.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 11: First Identification Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-first-id-img.webp" />
                  <source srcSet="/assets/images/fw-2g-first-id-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-first-id-img.png"
                    loading="lazy"
                    alt="Box 11: First Identification Number"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the winner&apos;s first identification number.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 12: Second Identification Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-second-id-img.webp" />
                  <source srcSet="/assets/images/fw-2g-second-id-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-second-id-img.png"
                    loading="lazy"
                    alt="Box 12: Second Identification Number"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the second identification number provided by the winner
                  at the time of payment, such as a driver’s license, passport,
                  or state-issued ID.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 13: State/Payer’s State Identification Number
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-id-number-img.webp" />
                  <source srcSet="/assets/images/fw-2g-id-number-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-id-number-img.png"
                    loading="lazy"
                    alt="Box 13: State/Payer’s State Identification Number"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the payer’s state abbreviation and the state
                  identification number assigned for state income tax reporting
                  purposes.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 14: State Winnings
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-state-winning-img.webp" />
                  <source srcSet="/assets/images/fw-2g-state-winning-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-state-winning-img.png"
                    loading="lazy"
                    alt="Box 14: State Winnings "
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of gambling winnings that are subject to
                  state income tax.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 15: State Income Tax Withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-state-income-img.webp" />
                  <source srcSet="/assets/images/fw-2g-state-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-state-income-img.png"
                    loading="lazy"
                    alt="Box 15: State Income Tax Withheld"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of state income tax that was withheld from
                  the winner’s gambling winnings, if any.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 16: Local Winnings
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-local-winning-img.webp" />
                  <source srcSet="/assets/images/fw-2g-local-winning-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-local-winning-img.png"
                    loading="lazy"
                    alt="Box 16: Local Winnings"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter any winnings that are subject to local (city or county)
                  income tax.
                </Typography>
                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 17: Local Income Tax withheld
                </Typography>
                <picture>
                  <source srcSet="/assets/images/fw-2g-local-income-img.webp" />
                  <source srcSet="/assets/images/fw-2g-local-income-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-local-income-img.png"
                    loading="lazy"
                    alt="Box 17: Local Income Tax withheld"
                    width={216}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>

                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the amount of local income tax that was withheld from
                  the winner’s gambling winnings, if any.
                </Typography>

                <Typography
                  variant="h4"
                  sx={{
                    fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                    mb: 0.5,
                    fontWeight: 600,
                  }}
                >
                  Box 18: Name of Locality
                </Typography>

                <picture>
                  <source srcSet="/assets/images/fw-2g-locality-img.webp" />
                  <source srcSet="/assets/images/fw-2g-locality-img.png" />
                  <Box
                    component="img"
                    src="/assets/images/fw-2g-locality-img.png"
                    loading="lazy"
                    alt="Box 18: Name of Locality"
                    width={217}
                    height={78}
                    className="img-responsive"
                    sx={{
                      border: '1px solid #333',
                      maxWidth: '100%',
                      mb: 1,
                    }}
                  />
                </picture>
                <Typography variant="body1" sx={{ mb: 3 }}>
                  Enter the name of the city, town, or other locality that
                  collected local tax.
                </Typography>

                <Alert severity="info" icon={false} sx={{ borderRadius: 2 }}>
                  <Accordion
                    // defaultExpanded
                    sx={{
                      '&.MuiAccordion-root::before, &.MuiAccordion-root.Mui-expanded::before':
                        {
                          background: 'none !important',
                        },
                      '&.MuiAccordion-root': { background: 'none !important' },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls=""
                      id=""
                      sx={{
                        px: 0,
                        m: 0,
                        justifyContent: 'flex-start',
                        '& .MuiAccordionSummary-content, & .MuiAccordionSummary-content.Mui-expanded':
                          { m: '0 !important', flexGrow: 0 },
                      }}
                    >
                      <Typography
                        variant="body1"
                        sx={{
                          fontWeight: 600,
                          color: '#014361',
                          fontSize: 16,
                        }}
                      >
                        Sources and References
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 0 }}>
                      <Typography
                        component="div"
                        variant="body2"
                        sx={{ mt: 1 }}
                      >
                        The information given above is based on IRS guidelines.
                        For more details, please refer the{' '}
                        <Link
                          href="https://www.irs.gov/pub/irs-pdf/iw2g.pdf"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          instructions of Form W-2G
                        </Link>{' '}
                        and{' '}
                        <Link
                          href="https://www.irs.gov/instructions/i1099gi"
                          sx={{ color: '#0202ff' }}
                          target="_blank"
                        >
                          Certain instructions of Information returns
                        </Link>
                        .
                      </Typography>
                    </AccordionDetails>
                  </Accordion>
                </Alert>
              </Paper>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Container maxWidth="lg" sx={{ mb: 5, my: { xs: 6, sm: 6, md: 6 } }}>
        <Stack
          direction={{ xs: 'column', md: 'row' }}
          spacing={{ xs: 2, md: 5 }}
        >
          <Box
            sx={{
              width: '100%',
              textAlign: 'center',
            }}
          >
            <Typography
              variant="h2"
              sx={{
                fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                fontWeight: 600,
                mb: 2,
                textAlign: 'center',
                lineHeight: 1.5,
              }}
            >
              Ready to file Form W-2G?
            </Typography>
            <Typography
              sx={{
                fontSize: { lg: 20, md: 18, sm: 16, xs: 16 },
                fontWeight: 500,
                color: '#333333',
                mb: 2,
              }}
            >
              Report your gambling winnings easily and on time to stay
              compliant.
            </Typography>

            <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
              <Button
                variant="contained"
                sx={{
                  mt: 1,
                  transition: 'all 0.3s ease 0s',
                  '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                }}
                className="btn-primary"
                href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=w2Ginstructions`}
              >
                Start filing Now!
              </Button>
            </Box>
          </Box>
        </Stack>
      </Container>
    </AppLayout>
  );
};

export default FormW2GInstructions;
